import { useState } from "react";
import config from "config/config";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useBreadcrumb } from "hooks/useBreadcrumb";

import Toast from "components/General/Toast";
import PaymentDetailCard from "components/Payments/PaymentDetailCard";
import SimpleCallToActionCard from "components/General/SimpleCallToActionCard";
import DeprecatedPaymentDetailCard from "components/Payments/DeprecatedPaymentDetailCard";
import AnnuityPaymentDetailCard from "components/Payments/Annuity/AnnuityPaymentDetailCard";

import { isShowSectionForAnnuityPayout } from "js/utilities/isShowSectionForAnnuityPayout";
import {
  getTypeOfPolicy,
  isFlagEnabled,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";

export default function Payment() {
  const { t } = useTranslation();
  useBreadcrumb([
    { name: t("CustomerPaymentBreadcrumb-home"), url: "/policies" },
    { name: t("CustomerPaymentBreadcrumb-payments"), url: "#" },
  ]);
  const ANNUITY = "annuity";
  const LIFE = "life";
  const [selectedPaymentType, setSelectedPaymentType] = useState(LIFE);

  const { policies } = useSelector((state) => state.data);
  const EXCLUDED_STATUSES = [
    "reducedpaidup",
    "paid-up",
    "extended term",
    "terminated",
  ];

  const isPolicyExcluded = (status) => {
    if (!status) return false;
    return EXCLUDED_STATUSES.includes(status.toLowerCase());
  };

  const isLZLQLOPPolicy = (policy) => {
    const productType = policy?.product?.type?.toUpperCase();
    return ["LZ", "LQ", "LOP"].includes(productType);
  };

  const annuityContracts =
    // everlake will not display inactive policies
    config.COMPANY_NAME === "everlake"
      ? policies.filter(
          (policy) =>
            getTypeOfPolicy(policy?.product?.type) === ANNUITY &&
            !isPolicyInactiveByPolicyStatus(policy?.status) &&
            !isPolicyExcluded(policy?.status)
        )
      : policies.filter(
          (policy) =>
            getTypeOfPolicy(policy?.product?.type) === ANNUITY &&
            !isPolicyExcluded(policy?.status)
        );

  const lifePolicies =
    // everlake will not display inactive policies
    config.COMPANY_NAME === "everlake"
      ? policies.filter(
          (policy) =>
            getTypeOfPolicy(policy?.product?.type) !== ANNUITY &&
            !isLZLQLOPPolicy(policy) &&
            !isPolicyInactiveByPolicyStatus(policy?.status) &&
            !isPolicyExcluded(policy?.status)
        )
      : policies.filter(
          (policy) =>
            getTypeOfPolicy(policy?.product?.type) !== ANNUITY &&
            !isLZLQLOPPolicy(policy) &&
            !isPolicyExcluded(policy?.status)
        );

  // Select Annuities tab by default if there are no life policies
  if (
    isFlagEnabled("ENABLE_ANNUITIES") &&
    lifePolicies.length === 0 &&
    selectedPaymentType !== ANNUITY
  ) {
    setSelectedPaymentType(ANNUITY);
  }

  return (
    <>
      <div className="container mb-8">
        <h1>{t("CustomerPayment-title")}</h1>
        <p>{t("CustomerPayment-subTitle")}</p>
        <hr />
      </div>
      <div className="mb-8">
        <Toast
          title={t("Toast-paymentTitle")}
          message={t("Toast-paymentSubtitle")}
        />
      </div>
      <div className="container ui-component-spacing">
        <div className="ui-card px-2 p-1 md:px-4">
          {lifePolicies.length > 0 && (
            <button
              className={`ui-btn-tab ${
                selectedPaymentType === LIFE ? "ui-btn-tab-current" : ""
              }`}
              onClick={() => setSelectedPaymentType(LIFE)}
            >
              {t("CustomerPayment-tab1")}
            </button>
          )}
          {isFlagEnabled("ENABLE_ANNUITIES") && annuityContracts.length > 0 && (
            <button
              className={`ui-btn-tab ${
                selectedPaymentType === ANNUITY ? "ui-btn-tab-current" : ""
              }`}
              onClick={() => setSelectedPaymentType(ANNUITY)}
            >
              {t("CustomerPayment-tab2")}
            </button>
          )}
        </div>
        {selectedPaymentType === LIFE && (
          <>
            {isFlagEnabled("ENABLE_RECURRING_PAYMENTS")
              ? lifePolicies.map((policy, idx) => (
                  <PaymentDetailCard
                    key={`Policy ${policy.contractID} payment card - ${idx}`}
                    idx={idx}
                    data={policy}
                  />
                ))
              : lifePolicies.map((policy, idx) => (
                  <DeprecatedPaymentDetailCard
                    key={`Policy ${policy.contractID} payment card - ${idx}`}
                    idx={idx}
                    data={policy}
                  />
                ))}
          </>
        )}
        {selectedPaymentType === ANNUITY &&
          annuityContracts.map((policy, idx) => (
            <AnnuityPaymentDetailCard
              key={`Policy ${policy.contractID} payment card - ${idx}`}
              idx={idx}
              data={policy}
            />
          ))}
      </div>

      {isShowSectionForAnnuityPayout(policies) && (
        <div className="container ui-component-spacing">
          <h2>{t("CustomerPayment-title-2")}</h2>
          <p className="text-sm">{t("CustomerPayment-subTitle-2")}</p>
          <hr />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:gap-6">
            <SimpleCallToActionCard
              title={t("CustomerPayment-gridTitle-1")}
              desc={t("CustomerPayment-gridDescription-1")}
              ctaText={t("CustomerPayment-action")}
              ctaUrl="/help"
            />
            <SimpleCallToActionCard
              title={t("CustomerPayment-gridTitle-2")}
              desc={t("CustomerPayment-gridDescription-2")}
              ctaText={t("CustomerPayment-action")}
              ctaUrl="/help"
            />
            <SimpleCallToActionCard
              title={t("CustomerPayment-gridTitle-3")}
              desc={t("CustomerPayment-gridDescription-3")}
              ctaText={t("CustomerPayment-action")}
              ctaUrl="/services-claims/forms"
            />
          </div>
        </div>
      )}
    </>
  );
}
